/**
 * aiClient.ts
 * 前端 AI 配置与模型客户端。
 * 各供应商密钥保存在服务端；此处仅查询密钥状态、模型列表与手动验证。
 * 对话/创作的实际调用由 src/ai（前端 Agent 循环）经 /api/ai/llm 代理完成。
 */

import type { AIConfigStatus, AIProvider, AIProviderConfig, AIVerifyResult, AIModel, AIUsageSummary } from '@xiaotianshu/shared/ai';
import { logger } from '../utils/logger';

function handleSessionExpired() {
  document.cookie = 'sw_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  sessionStorage.removeItem('sw_authenticated');
  window.location.hash = '#/';
}

async function jsonFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method || 'GET').toUpperCase();
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...((options.headers as Record<string, string>) || {}) };
  const r = await fetch(path, { ...options, headers, credentials: 'include' });
  logger.setRequestId(r.headers.get('X-Request-Id') || undefined);
  if (r.status === 401) {
    handleSessionExpired();
    throw new Error('会话已过期，请重新验证');
  }
  if (!r.ok) {
    const e = await r.json().catch(() => ({ error: r.statusText }));
    logger.warn('ai api request failed', { path, method, status: r.status, error: e.error });
    throw new Error(e.error || r.statusText);
  }
  return r.json() as Promise<T>;
}

// ── 配置与模型 ────────────────────────────────────────────────────

export function getAIConfig(): Promise<AIConfigStatus> {
  return jsonFetch<AIConfigStatus>('/api/ai/config');
}

export function setAIConfig(
  apiKey: string,
  opts?: { provider?: AIProvider; makeDefault?: boolean },
): Promise<AIConfigStatus> {
  const body: Record<string, unknown> = { provider: opts?.provider };
  // 仅在输入了非空 key 时才写入，避免空输入意外覆盖已有密钥
  if (apiKey.trim()) body.apiKey = apiKey;
  if (opts?.makeDefault) body.makeDefault = true;
  return jsonFetch<AIConfigStatus>('/api/ai/config', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/** 仅设置默认供应商（不触碰密钥） */
export function setDefaultProvider(provider: AIProvider): Promise<AIConfigStatus> {
  return jsonFetch<AIConfigStatus>('/api/ai/config', {
    method: 'PUT',
    body: JSON.stringify({ provider, makeDefault: true }),
  });
}

/** 手动验证某供应商密钥，更新有效状态并返回结果 + 最新配置状态 */
export function verifyProvider(provider: AIProvider): Promise<{ result: AIVerifyResult; status: AIConfigStatus }> {
  return jsonFetch<{ result: AIVerifyResult; status: AIConfigStatus }>('/api/ai/verify', {
    method: 'POST',
    body: JSON.stringify({ provider }),
  });
}

/** 获取近 N 天的 AI 调用用量统计；bookId 可选，按作品过滤 */
export function getAIUsage(days = 30, bookId?: string): Promise<AIUsageSummary> {
  const params = new URLSearchParams({ days: String(days) });
  if (bookId) params.set('bookId', bookId);
  return jsonFetch<AIUsageSummary>(`/api/ai/usage?${params.toString()}`);
}

/**
 * 获取模型列表。无密钥时返回 { error, code:'no_key' }（不抛出）。
 * provider 可选，指定要拉取模型列表的供应商（缺省为默认供应商）。
 */
export async function getAIModels(provider?: AIProvider): Promise<{ models?: AIModel[]; error?: string; code?: string }> {
  const qs = provider ? `?provider=${encodeURIComponent(provider)}` : '';
  const r = await fetch(`/api/ai/models${qs}`, { credentials: 'include' });
  if (r.status === 401) {
    handleSessionExpired();
    throw new Error('会话已过期，请重新验证');
  }
  const data = (await r.json().catch(() => ({}))) as { models?: AIModel[]; error?: string; code?: string };
  return data;
}

/**
 * 供应商是否可用于 AI 对话/助手（供模块选择）。
 *  - deepseek：配置了 key 且未验证为无效即可用（向后兼容）。
 *  - openrouter：必须显式验证为有效（valid === true）才可用。
 */
export function isProviderUsable(p: AIProviderConfig): boolean {
  if (!p.hasKey) return false;
  if (p.provider === 'openrouter') return p.valid === true;
  return p.valid !== false;
}

/** 从配置状态中筛选出可用的供应商 */
export function usableProviders(status: AIConfigStatus): AIProviderConfig[] {
  return status.providers.filter(isProviderUsable);
}
